import axios from 'axios';
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/lib/AuthContext";
import { usePermissions } from "@/lib/PermissionsContext";
import { AlertTriangle, X, RefreshCw } from "lucide-react";

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';
const CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

export function AIHealthMonitor() {
  const { user } = useAuth();
  const { canDo } = usePermissions();
  // 🔀 [Phase v13.61 Vague 5b — 2026-08-25] Branchement matrice via `ai_health_voir`.
  //   Seed préserve : owner only (comportement inchangé).
  const canView = canDo('ai_health_voir');
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    if (!canView) return;
    try {
      const r = await axios.get(`${API_URL}/api/monitoring/ai-health`);
      if (r.status === 200) {
        const d = r.data;
        setHealth(d);
        // Réinitialiser le dismiss si de nouvelles erreurs sont détectées
        if (d.has_errors) setDismissed(false);
      }
    } catch { /* silencieux */ }
  }, [canView]);

  useEffect(() => {
    check();
    const interval = setInterval(check, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, [check]);

  const clearErrors = async () => {
    await axios.delete(`${API_URL}/api/monitoring/ai-errors`);
    setHealth(null);
    setDismissed(true);
  };

  if (!canView) return null;
  if (!health?.has_errors || dismissed) return null;

  return (
    <div
      data-testid="ai-health-alert"
      className="fixed top-4 right-4 z-[9998] max-w-sm bg-red-50 border border-red-200 rounded-xl shadow-lg p-4"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-red-800">
            Alerte — Service IA dégradé
          </p>
          <p className="text-xs text-red-600 mt-1">
            {health.error_count} erreur{health.error_count > 1 ? "s" : ""} IA détectée{health.error_count > 1 ? "s" : ""} dans les dernières 48h.
            Certaines fonctionnalités (Chat IA, suggestions RDV, Croissance IA) peuvent être affectées.
          </p>
          {health.recent_errors?.[0] && (
            <p className="text-[10px] text-red-400 mt-1 font-mono truncate">
              {health.recent_errors[0].error?.slice(0, 80)}...
            </p>
          )}
          <p className="text-xs text-red-500 mt-1.5">
            Vérifiez votre budget OpenAI Platform ou rechargez votre clé.
          </p>
          <div className="flex gap-2 mt-2">
            <button
              onClick={clearErrors}
              className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium"
            >
              <RefreshCw className="w-3 h-3" /> Effacer les alertes
            </button>
          </div>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-red-300 hover:text-red-500 flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
